/**
 * Voice Router — Two-tier intent classification from transcribed text.
 *
 * Tier 1: Keyword / regex matching (instant, zero allocation beyond the result).
 * Tier 2: Semantic embedding similarity (optional fallback when Tier 1 yields low confidence).
 *
 * All processing is local. No network calls.
 */
import type { VoiceIntent, IntentType } from '../types/index';

// ---------------------------------------------------------------- pattern table

interface IntentPattern {
  type: IntentType;
  patterns: RegExp[];
  /** If true, the pattern capture group(s) may contain a drug name */
  extractsDrug: boolean;
}

const INTENT_PATTERNS: IntentPattern[] = [
  {
    type: 'next_dose',
    patterns: [
      /\bnext\s+dose\b/i,
      /\bwhat\s+do\s+i\s+take\s+next\b/i,
      /\bwhat'?s?\s+next\b/i,
      /\bupcoming\s+dose\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'drug_info',
    patterns: [
      /\btell\s+me\s+about\s+(.+)/i,
      /\bwhat\s+is\s+(.+)/i,
      /\binfo\s+(?:on|about)\s+(.+)/i,
      /\binformation\s+(?:on|about)\s+(.+)/i,
    ],
    extractsDrug: true,
  },
  {
    type: 'side_effects',
    patterns: [
      /\bside\s+effects?\b/i,
      /\breactions?\b/i,
      /\badverse\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'interactions',
    patterns: [
      /\binteract(?:ion)?s?\b/i,
      /\bcan\s+i\s+take\s+.+\s+with\b/i,
      /\bmix\b/i,
      /\bcombine\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'food_interactions',
    patterns: [
      /\bgrapefruit\b/i,
      /\bfood\b/i,
      /\beat\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'log_taken',
    patterns: [
      /\bi\s+(?:just\s+)?took\b/i,
      /\btook\s+my\b/i,
      /\btaken\b/i,
      /\blog\s+(?:that\s+)?(?:i\s+)?took\b/i,
    ],
    extractsDrug: true,
  },
  {
    type: 'log_skipped',
    patterns: [
      /\bskip(?:ped|ping)?\b/i,
    ],
    extractsDrug: true,
  },
  {
    type: 'pill_identify',
    patterns: [
      /\blook\s+like\b/i,
      /\bwhat\s+does\s+.+\s+look\s+like\b/i,
      /\bshow\s+me\b/i,
      /\bpill\s+photo\b/i,
      /\bidentify\s+pill\b/i,
    ],
    extractsDrug: true,
  },
  {
    type: 'show_schedule',
    patterns: [
      /\bschedule\b/i,
      /\btimeline\b/i,
      /\bmy\s+medications?\b/i,
      /\bmy\s+meds?\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'add_medication',
    patterns: [
      /\badd\b/i,
      /\bnew\s+medication\b/i,
      /\bnew\s+drug\b/i,
      /\bnew\s+med\b/i,
    ],
    extractsDrug: true,
  },
  {
    type: 'why_taking',
    patterns: [
      /\bwhy\b.*\b(?:taking|prescribed|on\s+this|need\s+this)\b/i,
      /\bwhat\s+(?:does\s+(?:it|this)\s+)?treat\b/i,
      /\bwhat\s+is\s+(?:it|this)\s+(?:used\s+)?for\b/i,
      /\bwhat\s+does\s+(?:it|this)\s+help\s+with\b/i,
      /\bwhat\s+is\s+it\s+for\b/i,
      /\bwhy\s+(?:do\s+)?i\s+(?:take|need)\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'missed_dose',
    patterns: [
      /\bmiss(?:ed)?\s+(?:a\s+|my\s+)?dose\b/i,
      /\bforgot\s+to\s+take\b/i,
      /\bskip(?:ped)?\s+(?:a\s+|my\s+)?dose\b/i,
      /\bdidn'?t\s+take\b/i,
      /\bwhat\s+if\s+i\s+miss\b/i,
      /\bi\s+forgot\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'side_effect_concern',
    patterns: [
      /\bshould\s+i\s+worry\b/i,
      /\bis\s+.+\s+normal\b/i,
      /\bconcerned\s+about\b/i,
      /\bexperiencing\b/i,
      /\bworried\s+about\b/i,
      /\bis\s+it\s+(?:okay|ok|safe)\s+(?:that|if)\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'how_works',
    patterns: [
      /\bhow\b.*\b(?:work|function)s?\b/i,
      /\bwhat\b.*\b(?:do(?:es)?\s+.*\s+(?:to|in)\s+(?:my\s+)?body|mechanism)\b/i,
      /\bhow\s+does\s+(?:it|this)\s+work\b/i,
      /\bwhat\s+does\s+(?:it|this)\s+do\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'storage',
    patterns: [
      /\bstor(?:e|age|ing)\b/i,
      /\brefrigerat/i,
      /\bkeep\b.*\bwhere\b/i,
      /\btemperature\b/i,
      /\bwhere\s+(?:do\s+i|should\s+i)\s+(?:keep|put|store)\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'overdose',
    patterns: [
      /\btoo\s+(?:much|many)\b/i,
      /\bdouble\s+dose\b/i,
      /\baccidentally\b.*\btook\b/i,
      /\boverdose\b/i,
      /\btook\s+(?:two|2|extra)\b/i,
      /\bwhat\s+if\s+i\s+take\s+too\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'pregnancy',
    patterns: [
      /\bpregnan/i,
      /\bbreastfeed/i,
      /\bnursing\b/i,
      /\bbaby\b/i,
      /\bexpecting\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'alcohol',
    patterns: [
      /\balcohol\b/i,
      /\bdrink(?:ing)?\b.*\b(?:with|while|on)\b/i,
      /\bbeer\b/i,
      /\bwine\b/i,
      /\bliquor\b/i,
      /\bcan\s+i\s+drink\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'driving',
    patterns: [
      /\bdriv(?:e|ing)\b/i,
      /\bdrowsy\b/i,
      /\bsleepy\b/i,
      /\boperate\b.*\bmachinery\b/i,
      /\bcan\s+i\s+drive\b/i,
      /\bwill\s+(?:it|this)\s+make\s+me\s+(?:drowsy|sleepy|tired)\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'stop_taking',
    patterns: [
      /\bstop\b.*\b(?:taking|medication|med)\b/i,
      /\bquit\b.*\b(?:taking|medication|med)\b/i,
      /\bdiscontinue\b/i,
      /\bwhat\b.*\bhappen\b.*\bstop\b/i,
      /\bcan\s+i\s+stop\b/i,
      /\bgo\s+off\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'generic_vs_brand',
    patterns: [
      /\bgeneric\b/i,
      /\bbrand\b.*\bname\b/i,
      /\bsame\s+thing\b/i,
      /\bdifferent\s+pill\b/i,
      /\bis\s+(?:it|this)\s+the\s+same\b/i,
      /\bwhy\b.*\b(?:look|color|shape)\b.*\bdifferent\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'cost',
    patterns: [
      /\bcost\b/i,
      /\bprice\b/i,
      /\bhow\s+much\b/i,
      /\bexpensive\b/i,
      /\bafford\b/i,
      /\bcheaper\b/i,
      /\bcoupon\b/i,
      /\binsurance\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'show_emr',
    patterns: [
      /\b(?:show|open|view|see)\s+(?:my\s+)?(?:medical\s+)?records?\b/i,
      /\b(?:my\s+)?(?:emr|ehr|health\s+record|medical\s+record)\b/i,
      /\b(?:show|open|view)\s+(?:my\s+)?chart\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'lab_question',
    patterns: [
      /\b(?:lab|labs|test)\s+results?\b/i,
      /\bblood\s+(?:work|test)\b/i,
      /\b(?:hba1c|a1c|hemoglobin|cholesterol|ldl|hdl|triglycerides|creatinine|glucose|potassium|sodium|egfr)\b/i,
      /\b(?:what|how)\b.*\b(?:lab|labs|blood)\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'emr_diagnosis',
    patterns: [
      /\b(?:my\s+)?(?:diagnos[ie]s|conditions?|diseases?|problems?)\b/i,
      /\bwhat\b.*\bwrong\s+with\s+me\b/i,
      /\bwhat\s+(?:do\s+i|conditions?\s+do\s+i)\s+have\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'emr_follow_up',
    patterns: [
      /\b(?:follow.?up|appointment|next\s+(?:doctor|visit)|when\s+do\s+i\s+(?:see|go))\b/i,
      /\bwho\s+is\s+my\s+(?:doctor|physician|provider)\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'emr_discharge_info',
    patterns: [
      /\bdischarge\b/i,
      /\bhospital\s+stay\b/i,
      /\bwhen\s+(?:did\s+i|was\s+i)\s+(?:admitted|discharged|released|leave)\b/i,
      /\bwhy\s+was\s+i\s+(?:in\s+the\s+)?hospital\b/i,
    ],
    extractsDrug: false,
  },
  {
    type: 'help',
    patterns: [
      /\bhelp\b/i,
      /\bcommands?\b/i,
      /\bwhat\s+can\s+you\s+do\b/i,
    ],
    extractsDrug: false,
  },
];

// ------------------------------------------------- semantic reference embeddings

/** Canonical sentences for Tier 2 semantic matching */
const INTENT_REFERENCE_SENTENCES: Record<IntentType, string> = {
  next_dose: 'what do I take next',
  drug_info: 'tell me about this medication',
  side_effects: 'what are the side effects',
  interactions: 'does this interact with other drugs',
  food_interactions: 'can I eat grapefruit with this',
  log_taken: 'I just took my medication',
  log_skipped: 'I am skipping this dose',
  pill_identify: 'what does this pill look like',
  show_schedule: 'show me my medication schedule',
  add_medication: 'add a new medication',
  help: 'what can you do',
  why_taking: 'why am I taking this medication',
  missed_dose: 'what if I miss a dose',
  side_effect_concern: 'should I worry about this side effect',
  how_works: 'how does this medication work in my body',
  storage: 'how should I store this medication',
  overdose: 'what if I take too much of this',
  pregnancy: 'is this safe during pregnancy or breastfeeding',
  alcohol: 'can I drink alcohol with this medication',
  driving: 'can I drive while taking this medication',
  stop_taking: 'can I stop taking this medication',
  generic_vs_brand: 'is the generic the same as the brand name',
  cost: 'how much does this medication cost',
  show_emr: 'show me my medical records',
  lab_question: 'what are my lab results',
  emr_diagnosis: 'what conditions do I have',
  emr_follow_up: 'when is my next doctor appointment',
  emr_discharge_info: 'tell me about my hospital stay',
  emr_allergies: 'what are my allergies',
  my_medications: 'what medications am I taking',
  symptom_concern: 'my feet are tingling should I be worried',
  emotional_concern: 'I am scared and overwhelmed',
  diet_question: 'can I eat rice and bread with my diabetes',
  otc_safe: 'can I take ibuprofen for my headache',
  warning_signs: 'what should I watch out for that means I need to go to the emergency room',
  overall_status: 'how am I doing overall',
  hospitalization_reason: 'why was I in the hospital',
  unknown: '',
};

// ----------------------------------------------------------------- drug extraction

/**
 * Attempt to pull a drug name from the raw text using simple heuristics.
 * Looks for the word immediately following contextual cue words.
 */
function extractDrugName(text: string): string | undefined {
  const cleaned = text.trim();

  // "tell me about Metformin" / "info on Lisinopril"
  const aboutMatch = cleaned.match(
    /(?:about|on|for|called|named|is)\s+([A-Za-z][A-Za-z\-]{2,})/i,
  );
  if (aboutMatch) return normalise(aboutMatch[1]);

  // "I took my Metformin" / "took my Atorvastatin"
  const tookMatch = cleaned.match(
    /(?:took|take|taking|skipping|skip|skipped)\s+(?:my\s+)?([A-Za-z][A-Za-z\-]{2,})/i,
  );
  if (tookMatch) return normalise(tookMatch[1]);

  // "what does Metformin look like"
  const lookMatch = cleaned.match(
    /what\s+does\s+([A-Za-z][A-Za-z\-]{2,})\s+look/i,
  );
  if (lookMatch) return normalise(lookMatch[1]);

  return undefined;
}

/** Capitalise the first letter, lowercase the rest. */
function normalise(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

// ----------------------------------------------------------------- cosine utils

function cosineSimilarity(a: Float32Array, b: Float32Array): number {
  let dot = 0;
  let normA = 0;
  let normB = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i] * b[i];
    normA += a[i] * a[i];
    normB += b[i] * b[i];
  }
  const denom = Math.sqrt(normA) * Math.sqrt(normB);
  return denom === 0 ? 0 : dot / denom;
}

// ------------------------------------------------------------ public classifier

const TIER1_CONFIDENCE_THRESHOLD = 0.7;
const TIER2_CONFIDENCE_THRESHOLD = 0.45;

/**
 * Classify a transcribed utterance into a VoiceIntent.
 *
 * @param text  Raw transcription text.
 * @param embedFn  Optional embedding function for Tier 2 semantic fallback.
 *                 If not provided, Tier 2 is skipped.
 */
export async function classifyIntent(
  text: string,
  embedFn?: (text: string) => Promise<Float32Array>,
): Promise<VoiceIntent> {
  const cleaned = text.trim().toLowerCase();

  if (cleaned.length === 0) {
    return { type: 'unknown', raw_text: text, confidence: 0 };
  }

  // ------- Tier 1: keyword / regex
  const tier1 = classifyTier1(cleaned, text);
  if (tier1.confidence >= TIER1_CONFIDENCE_THRESHOLD) {
    return tier1;
  }

  // ------- Tier 2: semantic embedding similarity (if available)
  if (embedFn) {
    const tier2 = await classifyTier2(cleaned, text, embedFn);
    if (tier2.confidence >= TIER2_CONFIDENCE_THRESHOLD) {
      // Prefer Tier 1 drug extraction even when Tier 2 wins on intent
      return {
        ...tier2,
        drug_name: tier2.drug_name ?? tier1.drug_name,
      };
    }
  }

  // ------- Fallback
  return {
    type: tier1.type !== 'unknown' ? tier1.type : 'unknown',
    drug_name: tier1.drug_name,
    raw_text: text,
    confidence: Math.max(tier1.confidence, 0.1),
  };
}

// ----------------------------------------------------------------- Tier 1

function classifyTier1(cleaned: string, rawText: string): VoiceIntent {
  let bestType: IntentType = 'unknown';
  let bestConfidence = 0;
  let drugFromCapture: string | undefined;

  for (const intent of INTENT_PATTERNS) {
    for (const pattern of intent.patterns) {
      const match = cleaned.match(pattern);
      if (match) {
        // Confidence is boosted by longer matches (more specific)
        const matchRatio = match[0].length / cleaned.length;
        const confidence = Math.min(0.6 + matchRatio * 0.4, 1.0);

        if (confidence > bestConfidence) {
          bestConfidence = confidence;
          bestType = intent.type;

          // Capture group may contain a drug name
          if (intent.extractsDrug && match[1]) {
            drugFromCapture = normalise(match[1].trim().split(/\s/)[0]);
          }
        }
      }
    }
  }

  const drugName = drugFromCapture ?? extractDrugName(rawText);

  return {
    type: bestType,
    drug_name: drugName,
    raw_text: rawText,
    confidence: bestConfidence,
  };
}

// ----------------------------------------------------------------- Tier 2

async function classifyTier2(
  _cleaned: string,
  rawText: string,
  embedFn: (text: string) => Promise<Float32Array>,
): Promise<VoiceIntent> {
  const inputVec = await embedFn(rawText);

  let bestType: IntentType = 'unknown';
  let bestScore = -1;

  const intentTypes = Object.keys(INTENT_REFERENCE_SENTENCES) as IntentType[];

  for (const intentType of intentTypes) {
    const refText = INTENT_REFERENCE_SENTENCES[intentType];
    if (!refText) continue;

    const refVec = await embedFn(refText);
    const score = cosineSimilarity(inputVec, refVec);

    if (score > bestScore) {
      bestScore = score;
      bestType = intentType;
    }
  }

  return {
    type: bestType,
    drug_name: extractDrugName(rawText),
    raw_text: rawText,
    confidence: bestScore,
  };
}
